import dayjs from 'dayjs'
import { FC, useMemo } from 'react'

import { FileApiType } from '@/api/shared/types'
import { cn } from '@/app/utils'
import { FileUploadedCard } from '@/components/shared/file-viewer'
import { LogoCircleIcon } from '@/components/shared/icons'
import { isFileImageByPath } from '@/utils/file'
import { makePhonesAndLinksClickable } from '@/utils/makePhonesAndLinksClickable'

type Props = {
  date: string | Date
  onLinkClick: (url: string) => void
  message?: string
  file?: FileApiType
  authorName?: string | null
  withDate?: boolean
  withLogo?: boolean
}

const CommentsServerMessage: FC<Props> = ({
  message,
  date,
  file,
  onLinkClick,
  withDate = true,
  withLogo = true,
  authorName
}) => {
  const isImage = useMemo(
    () => isFileImageByPath(file?.name || ''),
    [file?.name]
  )

  return (
    <>
      {message || file ? (
        <div
          data-testid="chat-server-message-id"
          className={cn(
            'flex w-max flex-col items-start',
            withDate ? 'mb-4 last:mb-0' : 'last:mb-0'
          )}
        >
          <div className="flex items-end gap-8">
            {withLogo ? <LogoCircleIcon /> : <div className="w-32" />}
            {message && (
              <div className="mb-2 flex max-w-[556px] flex-col gap-8 rounded-r-12 rounded-tl-12 bg-grey-10 p-12 lg:max-w-[476px] sm:max-w-[302px]">
                {authorName && (
                  <p
                    data-testid="chat-server-message-author-id"
                    className="font-bold text-14"
                  >
                    {authorName}
                  </p>
                )}
                <p
                  data-testid="chat-server-message-text-id"
                  className="whitespace-pre-line break-words font-pro text-deepBlack text-16"
                >
                  {makePhonesAndLinksClickable(
                    message,
                    onLinkClick,
                    'text-bronze-40 underline'
                  )}
                </p>
              </div>
            )}
            {file && (
              <div
                className={cn(
                  'mb-2 max-w-[556px] bg-grey-10 p-12 lg:max-w-[476px] sm:max-w-[302px]',
                  isImage ? 'rounded-12' : 'rounded-r-12 rounded-tl-12'
                )}
              >
                <FileUploadedCard
                  file={file}
                  inline={true}
                  imgSizeClassName="h-[180px] w-[180px]"
                />
              </div>
            )}
          </div>
          {withDate && (
            <p
              data-testid="chat-server-message-time-id"
              className="text-medium self-end font-pro text-grey-50 text-10"
            >
              {dayjs(date).format('HH:mm')}
            </p>
          )}
        </div>
      ) : (
        <></>
      )}
    </>
  )
}

export { CommentsServerMessage }
